import { jwtDecode, type JwtPayload } from 'jwt-decode';
import { writable, type Readable } from 'svelte/store';
import { type AuthenticationClient, type RestClient } from '@directus/sdk';
import { pin, logoutClick } from './stores';

const TOKEN_EXPIRATION_BUFFER = 300;

export interface LoginStatus {
	isLoggedIn: boolean;
	userId?: string;
}

export class PicpeccAuth {
	#onIsLoggedInChanged = writable<LoginStatus>();
	onIsLoggedInChanged: Readable<LoginStatus> = {
		subscribe: this.#onIsLoggedInChanged.subscribe
	};

	constructor(private directus: AuthenticationClient<any> & RestClient<any>) {
		this.onIsLoggedInChanged.subscribe((loginStatus) => {
			if (!loginStatus) {
				return;
			}

			this.#loginStatus = Promise.resolve(loginStatus);
		});

		this.#loginStatus = new Promise<LoginStatus>(async (resolve, _) => {
			try {
				let access_token = localStorage.getItem('access_token');
				if (!access_token) {
					const refresh_token_may_exist =
						localStorage.getItem('refresh_token_may_exist') === 'true';
					if (!refresh_token_may_exist) {
						const status: LoginStatus = { isLoggedIn: false };
						this.#onIsLoggedInChanged.set(status);
						resolve(status);
						return;
					}

					const tokens = await this.directus.refresh();
					if (!tokens.access_token) {
						throw new Error('no access token');
					}
					access_token = tokens.access_token;
				}

				var jwt = jwtDecode<JwtPayload>(access_token);
				if (jwt.exp && jwt.exp - Date.now() / 1000 < TOKEN_EXPIRATION_BUFFER) {
					const tokens = await this.directus.refresh();
					if (!tokens.access_token) {
						throw new Error('no access token');
					}
					access_token = tokens.access_token;
				}

				await this.directus.setToken(access_token);
				const status = await this.#updateLoginStatus(access_token);
				resolve(status);
			} catch (e) {
				console.error('could not restore login', e);
				localStorage.removeItem('refresh_token_may_exist');
				const status: LoginStatus = { isLoggedIn: false };
				this.#onIsLoggedInChanged.set(status);
				resolve(status);
			}
		});
	}

	#loginStatus: Promise<LoginStatus>;

	#updateLoginStatus(access_token: string) {
		localStorage.setItem('access_token', access_token);
		try {
			const jwtPayload: JwtPayload & { id?: string } = jwtDecode(access_token);
			var status: LoginStatus = { isLoggedIn: true, userId: jwtPayload.id ?? jwtPayload.sub };
			localStorage.setItem('refresh_token_may_exist', 'true');
			this.#onIsLoggedInChanged.set(status);
			return status;
		} catch (e) {
			var status: LoginStatus = { isLoggedIn: false };
			this.#onIsLoggedInChanged.set(status);
			return status;
		}
	}

	async getIsLoggedIn() {
		return (await this.#loginStatus).isLoggedIn;
	}

	async login(email: string, password: string, options?: { otp?: string }) {
		this.#onIsLoggedInChanged.set({ isLoggedIn: false });
		var tokens = await this.directus.login(email, password, { ...options, mode: 'cookie' });
		if (!tokens.access_token) {
			throw new Error('no access token');
		}

		console.log('properly logged in');
		this.#updateLoginStatus(tokens.access_token);
		pin.set(false);
	}

	async logout() {
		localStorage.removeItem('access_token');
		localStorage.removeItem('refresh_token_may_exist');
		this.#onIsLoggedInChanged.set({ isLoggedIn: false });
		logoutClick.set(true);
		await this.directus.logout();
	}
}
